/**
 * LE ISTRUZIONI DELLA PARTITA ESTERNA, lette per chiave — e il TAVOLO del backgammon.
 *
 * 🔴 **Fino al 2026-09-25 la scheda d'incontro stampava `externalMatch.instructions` GREZZO**
 * (`JSON.stringify`), con la ragione «passthrough senza contratto». Il contratto invece c'e' da
 * qTournaments 1.54.0 (kb#203): `instructions = { text?, table? }`, due sottochiavi DICHIARATE
 * (`esternaDaDocumento`, `app-qtournaments/app/superficie/capability/incontri.js:75-110` sul servito
 * `qlash-limulo` 4e675a2): `text` e' la nota umana del provider (`metadata.instructions`), `table` e'
 * `{ matchId, endpoint }` — l'indirizzo del tavolo, «o entrambi o niente». Reperto di Luca (I-189,
 * torneo backgammon Back_5 su stg-tma): «Game status unknown» sopra
 * `{"text":"La partita e' aperta su QGammon…","table":{"matchId":…,"endpoint":…}}`, e nessun modo di
 * entrare al tavolo con la partita viva.
 * ⇒ Qui si leggono le due chiavi dichiarate; tutto il resto e' «illeggibile» e a schermo diventa UNA
 * frase generica. ⛔ Un JSON che la TMA non sa leggere non si stampa MAI grezzo
 * (`tools/verify-limulo.ts` lo vieta).
 *
 * ⚠️ `status` qui non c'entra: il motore ne scrive solo tre (`conflict|failed|cancelled`,
 * `externalData.ts`), quindi su un tavolo aperto arriva `''` — che `externalMatchStatusLabelOf` legge
 * `unknown`. Non e' un tavolo sconosciuto: e' uno stato che nessuno ha scritto.
 *
 * Puro: lo esegue anche il gate sotto Node.
 */
import type { LimuloMatch } from '../../types/limulo.ts';
import { chessMatchIdValido, chessMatchPath } from '../chess/tavolo.ts';
import { externalMatchDeadOf } from './externalData.ts';
import { matchStateOf } from './match.ts';

/**
 * Il `profileType` del backgammon nel catalogo giochi. ⚠️ Stesso valore di `BACKGAMMON_PROFILE_TYPE`
 * in `lib/qgammon/lobbyTornei.ts`, ripetuto qui perche' quel file importa per alias e questo deve
 * girare sotto Node (il gate). Il gate controlla che i due non divergano.
 */
export const TAVOLO_PROFILE_TYPE = 'backgammon';

/** Il `profileType` degli scacchi nel catalogo (`CHESS_PROFILE_TYPE`, `lib/limulo/giocoSelezionato.ts`: il gate li confronta). */
export const TAVOLO_PROFILE_TYPE_SCACCHI = 'chess';

export interface LimuloTavolo {
  /** L'id della partita sul game host: su QGammon e' quello della rotta `/backgammon/match/:id`, sugli scacchi `/chess/match/:id`. */
  matchId: string;
  /** L'endpoint socket del servizio di gioco. Non si apre: dice solo che il tavolo e' servito. */
  endpoint: string;
}

/** Il gioco di un tavolo: dice a `useEntraAlTavolo` QUALE porta usare. */
export type GiocoAlTavolo = 'backgammon' | 'chess';

/** Cio' che serve a decidere se un tavolo e' «servito» da QUESTA build. Lo legge chi chiama (qui niente `import.meta.env`). */
export interface ContestoTavoli {
  /** Il QGammon della build (`servizioTavoliDellaBuild()`), o `null` se la build non ha il backgammon. */
  servizio: string | null;
  /** Gli scacchi su Limulo sono accesi in questa build (`chessLimuloAcceso()`): solo allora la rotta `chess/match/:id` esiste. */
  scacchi: boolean;
}

interface RigaTavolo {
  gioco: GiocoAlTavolo;
  /** La rotta del tavolo di QUESTA partita. */
  rotta: (matchId: string) => string;
  /** Il tavolo e' servito da questa build? */
  servito: (tavolo: LimuloTavolo, contesto: ContestoTavoli) => boolean;
}

/**
 * LA TABELLA `profileType → {gioco, rotta, servizio}` (P2 del piano, P4b la riempie per gli scacchi). Un tavolo si riconosce dal
 * GIOCO del torneo (torneo → `gameId` → catalogo), mai dalla forma del JSON.
 *  · backgammon: la rotta si collega SEMPRE al QGammon della build e ignora l'`endpoint` del torneo, quindi il tavolo e' servito
 *    solo se l'endpoint e' lo stesso host (`stessoServizio`);
 *  · scacchi: il socket NON dipende da un host della build — l'indirizzo lo da' il BFF col ticket, a ogni connessione — quindi
 *    non c'e' un host da confrontare: e' servito se la rotta esiste in questa build (`contesto.scacchi`) e l'id e' un uuid
 *    (un id che il tavolo rifiuterebbe non si offre).
 * ⛔ Cercata con `Object.hasOwn`: un `profileType` sconosciuto — o `constructor`, `__proto__` — NON ha un tavolo.
 */
const TAVOLI: Readonly<Record<string, RigaTavolo>> = {
  [TAVOLO_PROFILE_TYPE]: {
    gioco: 'backgammon',
    rotta: (matchId) => `/backgammon/match/${matchId}`,
    servito: (tavolo, contesto) => stessoServizio(tavolo.endpoint, contesto.servizio),
  },
  [TAVOLO_PROFILE_TYPE_SCACCHI]: {
    gioco: 'chess',
    rotta: chessMatchPath,
    servito: (tavolo, contesto) => contesto.scacchi && chessMatchIdValido(tavolo.matchId),
  },
};

const rigaDi = (profileType: string | null | undefined): RigaTavolo | null =>
  typeof profileType === 'string' && Object.hasOwn(TAVOLI, profileType) ? TAVOLI[profileType] : null;

/** La rotta del tavolo di un gioco (per chi entra: `useEntraAlTavolo`). */
export const rottaDelTavolo = (gioco: GiocoAlTavolo, matchId: string): string =>
  (gioco === 'chess' ? TAVOLI[TAVOLO_PROFILE_TYPE_SCACCHI] : TAVOLI[TAVOLO_PROFILE_TYPE]).rotta(matchId);

export interface LimuloIstruzioniEsterne {
  /** La nota umana del provider, cosi' com'e' (e' nella sua lingua). `null` = non c'e'. */
  testo: string | null;
  /** Il tavolo, solo se ha ENTRAMBI i campi. */
  tavolo: LimuloTavolo | null;
  /** C'era qualcosa, e non e' nessuna delle due chiavi dichiarate: si dice con una frase generica. */
  illeggibili: boolean;
}

const asRecord = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

const testoDi = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v.trim() : null;

export function istruzioniEsterneOf(instructions: unknown): LimuloIstruzioniEsterne {
  const r = asRecord(instructions);
  const testo = testoDi(r.text);
  const t = asRecord(r.table);
  const matchId = testoDi(t.matchId);
  const endpoint = testoDi(t.endpoint);
  // «O entrambi o niente», come la scrittura del motore: mezzo tavolo non porta da nessuna parte.
  const tavolo = matchId !== null && endpoint !== null ? { matchId, endpoint } : null;
  const qualcosa = instructions !== null && instructions !== undefined && Object.keys(r).length > 0;
  return { testo, tavolo, illeggibili: qualcosa && testo === null && tavolo === null };
}

/**
 * L'`endpoint` del tavolo e' lo STESSO servizio QGammon che questa build usa (`env.qgammonWsUrl`)?
 *
 * 🔴 La rotta `/backgammon/match/:id` si collega SEMPRE al QGammon della build, e ignora l'`endpoint`
 * che il torneo pubblica (revisione di tma-orcb su #391). Se i due differiscono — un torneo di staging
 * visto da una build con un altro QGammon — il bottone porterebbe a un tavolo che li' non esiste.
 * ⇒ Si confronta l'HOST (nome e porta), non l'origine intera: lo stesso gameserver si scrive
 * `https://…` come base REST e `wss://…` come base del socket (il default di produzione e' `wss://`),
 * e le due forme sono lo stesso servizio. Un indirizzo che non si legge non e' «lo stesso».
 */
export function stessoServizio(endpoint: string, servizio: string | null | undefined): boolean {
  if (!servizio) return false;
  try {
    return new URL(endpoint).host === new URL(servizio).host;
  } catch {
    return false;
  }
}

/** Gli stati in cui c'e' ancora una partita da giocare: gli stessi di «apri il gioco» (`openGame.ts`). */
const DA_GIOCARE = new Set(['pending', 'proposed']);

/**
 * Il tavolo in cui entrare da QUESTO incontro, o `null`.
 *
 * Riconosciuto dal GIOCO del torneo (`profileType === 'backgammon'`, torneo → `gameId` → catalogo),
 * mai dalla forma del JSON: un `table` che arrivasse da un altro provider non e' una rotta QGammon.
 * E solo su un incontro ancora da giocare, con la partita esterna non morta (`externalMatchDeadOf`),
 * e un tavolo servito dallo stesso QGammon della build (`stessoServizio`).
 */
export function tavoloDaAprireOf(input: {
  match: LimuloMatch;
  profileType: string | null | undefined;
  /**
   * Il QGammon di QUESTA build (`env.qgammonWsUrl`), o `null` se la build non ha il backgammon — e
   * allora la rotta non esiste e nessun tavolo si apre. Lo passa chi legge l'env: qui non si legge.
   */
  servizio: string | null;
  /**
   * Gli scacchi su Limulo sono accesi in questa build (`chessLimuloAcceso()`): senza, la rotta `chess/match/:id` non esiste e
   * nessun tavolo di scacchi si apre. Facoltativo: assente = spento, come prima di P4b.
   */
  scacchi?: boolean;
  /**
   * Gli id QGammon delle partite che questa sessione ha visto FINIRE (`activeMatch.finite`, #403).
   * Un tavolo fra questi non si apre: a partita finita l'incontro resta `pending` sul server finche'
   * il motore non lo chiude (~45 s, prova Back_6 del 26/09), e la scheda offriva «Entra al tavolo»
   * su una partita gia' giocata. E' la stessa regola di `tavoliFiniti` in `azioniDelTorneo`
   * (`azionePersistente.ts`), letta qui perche' il bottone unico e il riquadro della scheda passano da
   * questa funzione. Facoltativo: assente = nessuna partita finita.
   */
  finiti?: ReadonlySet<string>;
}): (LimuloTavolo & { gioco: GiocoAlTavolo }) | null {
  const riga = rigaDi(input.profileType);
  if (riga === null) return null;
  if (!DA_GIOCARE.has(matchStateOf(input.match))) return null;
  const esterna = input.match.externalMatch;
  if (esterna === null || externalMatchDeadOf(esterna)) return null;
  const tavolo = istruzioniEsterneOf(esterna.instructions).tavolo;
  if (tavolo === null || !riga.servito(tavolo, { servizio: input.servizio, scacchi: input.scacchi === true })) return null;
  return input.finiti?.has(tavolo.matchId) ? null : { ...tavolo, gioco: riga.gioco };
}

/**
 * Le istruzioni di QUESTO incontro parlano di un tavolo backgammon che per chi guarda NON e' piu'
 * aperto: l'incontro non e' piu' da giocare (c'e' un punteggio), la partita esterna e' morta, oppure
 * questa sessione ha visto la partita finire (`finiti`, come sopra).
 *
 * 🔴 Serve alla NOTA del provider, non al bottone. La nota viene scritta all'apertura del tavolo
 * («La partita e' aperta su QGammon… il tavolo li aspetta») e il motore non la riscrive. Su un
 * incontro concluso restava a schermo sotto il punteggio depositato, insieme a «Stato del gioco
 * sconosciuto» (censimento del 26/09, Back_6 0–1). ⇒ quando il tavolo e' chiuso la scheda non la
 * mostra.
 * ⚠️ Non guarda `servizio`: una nota d'apertura e' falsa a tavolo chiuso anche se il tavolo stava su
 * un altro QGammon.
 */
export function tavoloChiusoOf(input: {
  match: LimuloMatch;
  profileType: string | null | undefined;
  finiti?: ReadonlySet<string>;
}): boolean {
  if (rigaDi(input.profileType) === null) return false;
  const esterna = input.match.externalMatch;
  if (esterna === null) return false;
  const tavolo = istruzioniEsterneOf(esterna.instructions).tavolo;
  if (tavolo === null) return false;
  if (!DA_GIOCARE.has(matchStateOf(input.match)) || externalMatchDeadOf(esterna)) return true;
  return input.finiti?.has(tavolo.matchId) ?? false;
}
